// CLIENT MIRROR of server lib/feeCalculator.js — keep in lockstep.
// Phase C: flat-fee estimate + total rounding are per-currency via
// utils/payinConfig.ts. Calls without a currency behave exactly as before
// (usd), so every legacy call site renders unchanged numbers.
import { flatFeeFor, roundUpToStep } from "./payinConfig";

export const PLATFORM_FEE_RATE = 0.15;
export const STRIPE_FEE_RATE = 0.035;
export const STRIPE_FLAT_FEE = 0.30; // usd/eur flat estimate — others come from payinConfig

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function roundUpToHalfOrDollar(n: number, currency?: string | null): number {
  return roundUpToStep(n, currency);
}

export function estimateStripeFee(amount: number, currency?: string | null): number {
  const a = Number(amount) || 0;
  if (a <= 0) return 0;
  return round2(a * STRIPE_FEE_RATE + flatFeeFor(currency));
}

export interface FeeBreakdown {
  baseAmount: number;
  customerTotal: number;
  applicationFee: number;
  djNetShare: number;
  estimatedStripeFee: number;
}

export function calculateFeeBreakdown(baseAmount: number, currency?: string | null): FeeBreakdown {
  const base = round2(Number(baseAmount) || 0);
  if (base <= 0) {
    return {
      baseAmount: 0,
      customerTotal: 0,
      applicationFee: 0,
      djNetShare: 0,
      estimatedStripeFee: 0,
    };
  }

  const estimatedStripeFeeOnBase = estimateStripeFee(base, currency);
  const preRoundTotal = base + estimatedStripeFeeOnBase;
  const customerTotal = roundUpToHalfOrDollar(preRoundTotal, currency);
  const estimatedStripeFeeOnTotal = estimateStripeFee(customerTotal, currency);
  const djNetShare = round2(base * (1 - PLATFORM_FEE_RATE));
  const applicationFee = Math.max(
    0,
    round2(customerTotal - estimatedStripeFeeOnTotal - djNetShare)
  );

  return {
    baseAmount: base,
    customerTotal,
    applicationFee,
    djNetShare,
    estimatedStripeFee: estimatedStripeFeeOnTotal,
  };
}
